/**
 * LocationPickerDialog — Google Places powered address picker.
 *
 * Invoked from OrgGate, OrganizationProfile (HQ card), and BranchesLocations
 * (Location dialog). One pick derives street / city / state / country
 * (ISO alpha-2) / postal_code / lat / lng / timezone (IANA).
 *
 * ---------------------------------------------------------------------------
 * Why we drive AutocompleteService manually instead of using
 * `@react-google-maps/api`'s `<Autocomplete>` wrapper:
 *
 *   - The wrapper mutates the underlying <input>. When that <input> is
 *     owned by MUI TextField, the wrapper's setup race can silently
 *     fail: the user types, no predictions ever appear, no errors in
 *     the console. Multiple production apps hit this.
 *   - Driving the service ourselves gives us a first-class MUI
 *     Autocomplete (keyboard nav, styling, loading spinner, no dropdown
 *     while empty) and a predictable call graph:
 *
 *         input → AutocompleteService.getPlacePredictions()
 *            → user selects
 *         → PlacesService.getDetails({place_id})
 *            → place.address_components + geometry
 *         → fetch() Google TimeZone REST API with lat/lng
 *         → onSelect(PickedPlace) → parent form filled.
 *
 * ---------------------------------------------------------------------------
 * Graceful degradation:
 *   - `REACT_APP_GOOGLE_MAPS_API_KEY` missing / `isLoaded===false` with
 *     a `loadError` → render a manual fallback form (country Select +
 *     city / state / postal / street TextFields) behind a warning
 *     banner. Never a blank screen.
 *
 * China mainland: Google Maps is blocked. This picker effectively
 * requires an out-of-PRC network. Swapping in Gaode / Baidu is a
 * future follow-up.
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloseOutlined,
  LocationOnOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import {
  GoogleMap,
  MarkerF,
} from '@react-google-maps/api';

import { countries } from '../../constants/enums/countries';
import { useTranslation } from 'react-i18next';
import {
  GoogleMapsErrorBoundary,
  GoogleMapsUnavailableDialog,
  useSvdyGoogleMaps,
} from './googleMaps';

// ---------------------------------------------------------------------------
// Public contract
// ---------------------------------------------------------------------------

export interface PickedPlace {
  street_address: string;
  city: string;
  state: string;
  country: string; // ISO 3166-1 alpha-2
  postal_code: string;
  lat: number | null;
  lng: number | null;
  timezone: string; // IANA (empty string if TimeZone API was unavailable)
  formatted_address: string;
}

export interface LocationPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (place: PickedPlace) => void;
  title?: string;
  initialAddress?: Partial<PickedPlace>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_CENTER = { lat: 22.3193, lng: 114.1694 }; // Hong Kong — a
// neutral default for the initial view; overridden on first pick.

// ---------------------------------------------------------------------------
// address_components → PickedPlace mapping
// ---------------------------------------------------------------------------

interface GAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

function extract(
  components: GAddressComponent[] | undefined,
  type: string,
  useShort = false,
): string {
  if (!components) return '';
  const hit = components.find((c) => c.types.includes(type));
  if (!hit) return '';
  return useShort ? hit.short_name : hit.long_name;
}

function buildStreet(components: GAddressComponent[] | undefined): string {
  const number = extract(components, 'street_number');
  const route = extract(components, 'route');
  if (number && route) return `${number} ${route}`;
  return route || number || '';
}

function buildCity(components: GAddressComponent[] | undefined): string {
  return (
    extract(components, 'locality') ||
    extract(components, 'postal_town') ||
    extract(components, 'administrative_area_level_2') ||
    extract(components, 'sublocality_level_1') ||
    ''
  );
}

/**
 * The street line when Google sends no `route` / `street_number` (owner report 2026-11-02: picking a place
 * filled city and country but left Street empty). Measured against the Places API that day:
 *   "131 Continental Drive, Newark" -> street_number + route        (US: fine)
 *   "Jinting North Road, Xiamen"    -> route only                   (fine)
 *   "Shibuya Scramble Crossing"     -> NO route: '21' and 'Udagawacho' come as `political`, the street
 *                                      line exists only in formatted_address "21 Udagawacho, Shibuya, …"
 * Japan, Korea, Hong Kong and most POIs / buildings are the third case. So the street is taken from
 * formatted_address: the comma segments BEFORE the first one that names the city, state, country or
 * postal code. Nothing is invented: if every segment is one of those, the street stays empty.
 */
export function streetFromFormatted(formatted: string, components: GAddressComponent[]): string {
  const known = ['locality', 'postal_town', 'administrative_area_level_1', 'administrative_area_level_2',
    'country', 'postal_code', 'sublocality_level_1']
    .flatMap((type) => components.filter((c) => c.types.includes(type)).flatMap((c) => [c.long_name, c.short_name]))
    .filter(Boolean)
    .map((v) => v.toLowerCase());
  const segments = formatted.split(/[,，、]/).map((x) => x.trim()).filter(Boolean);
  const out: string[] = [];
  for (const seg of segments) {
    const low = seg.toLowerCase();
    if (known.some((k) => low === k || low.startsWith(`${k} `) || low.endsWith(` ${k}`))) break;
    out.push(seg);
  }
  return out.length < segments.length ? out.join(', ') : '';
}

export function placeToPicked(
  place: google.maps.places.PlaceResult,
): Omit<PickedPlace, 'timezone'> {
  const components = (place.address_components ?? []) as GAddressComponent[];
  const lat = place.geometry?.location?.lat() ?? null;
  const lng = place.geometry?.location?.lng() ?? null;
  return {
    street_address: buildStreet(components) || streetFromFormatted(place.formatted_address ?? '', components),
    city: buildCity(components),
    state: extract(components, 'administrative_area_level_1'),
    country: extract(components, 'country', /* useShort */ true),
    postal_code: extract(components, 'postal_code'),
    lat,
    lng,
    formatted_address: place.formatted_address ?? '',
  };
}

// ---------------------------------------------------------------------------
// Google Time Zone API (REST)
// ---------------------------------------------------------------------------

async function fetchTimezone(
  lat: number,
  lng: number,
  apiKey: string,
): Promise<string> {
  if (!apiKey) return '';
  try {
    const ts = Math.floor(Date.now() / 1000);
    const url =
      `https://maps.googleapis.com/maps/api/timezone/json?` +
      `location=${lat},${lng}&timestamp=${ts}&key=${apiKey}`;
    const res = await fetch(url);
    if (!res.ok) return '';
    const data = await res.json();
    return data.status === 'OK' && typeof data.timeZoneId === 'string'
      ? data.timeZoneId
      : '';
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Manual fallback form
// ---------------------------------------------------------------------------

interface ManualForm {
  street_address: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
}

function manualFromInitial(initial?: Partial<PickedPlace>): ManualForm {
  return {
    street_address: initial?.street_address ?? '',
    city: initial?.city ?? '',
    state: initial?.state ?? '',
    country: initial?.country ?? '',
    postal_code: initial?.postal_code ?? '',
  };
}

// ---------------------------------------------------------------------------
// Prediction (what MUI Autocomplete options hold)
// ---------------------------------------------------------------------------

interface Prediction {
  place_id: string;
  description: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LocationPickerDialogLoaded: React.FC<LocationPickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  title = 'Pick a location',
  initialAddress,
}) => {
  const { t } = useTranslation();
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? '';
  const { isLoaded, loadError } = useSvdyGoogleMaps();

  // Places services — lazily instantiated once Google Maps JS has loaded.
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService =
    useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesHostRef = useRef<HTMLDivElement | null>(null);

  // UI state
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Prediction[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [resolvingPlace, setResolvingPlace] = useState(false);
  const [resolvingTz, setResolvingTz] = useState(false);
  const [picked, setPicked] = useState<PickedPlace | undefined>(undefined);
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [manual, setManual] = useState<ManualForm>(() =>
    manualFromInitial(initialAddress),
  );

  // Init AutocompleteService once the Maps JS API is loaded.
  // PlacesService needs a DOM node that's been laid out; we create it
  // lazily in resolvePlace() where the ref is guaranteed to be mounted.
  useEffect(() => {
    if (isLoaded && !autocompleteService.current) {
      autocompleteService.current =
        new google.maps.places.AutocompleteService();
      sessionToken.current =
        new google.maps.places.AutocompleteSessionToken();
      // eslint-disable-next-line no-console
      console.info('[LocationPicker] AutocompleteService ready');
    }
  }, [isLoaded]);

  /** Lazy-create PlacesService — safe to call many times. */
  const ensurePlacesService = useCallback(() => {
    if (placesService.current) return placesService.current;
    if (!isLoaded || typeof google === 'undefined') return null;
    // Prefer the mounted host ref. If for any reason it isn't there yet
    // (e.g. dialog transition race), fall back to creating a throwaway
    // div at document.body — PlacesService only uses the node for its
    // attribution, not for interactive rendering.
    const host =
      placesHostRef.current ??
      (() => {
        const d = document.createElement('div');
        d.style.cssText =
          'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
        document.body.appendChild(d);
        return d;
      })();
    placesService.current = new google.maps.places.PlacesService(host);
    // eslint-disable-next-line no-console
    console.info(
      '[LocationPicker] PlacesService ready (source=',
      placesHostRef.current ? 'dialog-ref' : 'document-body-fallback',
      ')',
    );
    return placesService.current;
  }, [isLoaded]);

  // Reset each time the dialog re-opens.
  useEffect(() => {
    if (open) {
      setInputValue('');
      setOptions([]);
      setPicked(undefined);
      setManual(manualFromInitial(initialAddress));
      if (
        typeof initialAddress?.lat === 'number' &&
        typeof initialAddress?.lng === 'number'
      ) {
        setCenter({ lat: initialAddress.lat, lng: initialAddress.lng });
      } else {
        setCenter(DEFAULT_CENTER);
      }
      // New session token per open — billing optimization.
      if (isLoaded && typeof google !== 'undefined') {
        sessionToken.current =
          new google.maps.places.AutocompleteSessionToken();
      }
    }
  }, [open, initialAddress, isLoaded]);

  // -------------------------------------------------------------------
  // Debounced prediction fetcher
  // -------------------------------------------------------------------
  const fetchPredictions = useCallback((q: string) => {
    if (!autocompleteService.current) {
      setOptions([]);
      return;
    }
    if (!q || q.trim().length < 2) {
      setOptions([]);
      return;
    }
    setOptionsLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input: q,
        sessionToken: sessionToken.current ?? undefined,
      },
      (predictions, status) => {
        setOptionsLoading(false);
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          setOptions([]);
          return;
        }
        setOptions(
          predictions.map((p) => ({
            place_id: p.place_id,
            description: p.description,
          })),
        );
      },
    );
  }, []);

  // 180ms debounce on typing.
  useEffect(() => {
    if (!inputValue) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => fetchPredictions(inputValue), 180);
    return () => clearTimeout(timer);
  }, [inputValue, fetchPredictions]);

  // -------------------------------------------------------------------
  // Resolve a prediction → full place details + async timezone
  // -------------------------------------------------------------------
  const resolvePlace = useCallback(
    (placeId: string) => {
      const svc = ensurePlacesService();
      if (!svc) {
        // eslint-disable-next-line no-console
        console.warn('[LocationPicker] resolvePlace: Google Maps not loaded yet');
        return;
      }
      // eslint-disable-next-line no-console
      console.info('[LocationPicker] resolvePlace(', placeId, ')');
      setResolvingPlace(true);
      svc.getDetails(
        {
          placeId,
          fields: [
            'address_components',
            'formatted_address',
            'geometry.location',
            'name',
          ],
          sessionToken: sessionToken.current ?? undefined,
        },
        async (place, status) => {
          setResolvingPlace(false);
          // eslint-disable-next-line no-console
          console.info(
            '[LocationPicker] getDetails status=',
            status,
            'hasPlace=',
            !!place,
          );
          if (
            status !== google.maps.places.PlacesServiceStatus.OK ||
            !place
          ) {
            // eslint-disable-next-line no-console
            console.warn(
              '[LocationPicker] getDetails failed. Most likely causes: (1) Places API (New) not enabled on this API key in GCP console; (2) API key has referrer restrictions that do not include localhost:4000; (3) Billing not enabled on the GCP project.',
            );
            return;
          }
          const base = placeToPicked(place);
          setPicked({ ...base, timezone: '' });
          if (base.lat !== null && base.lng !== null) {
            setCenter({ lat: base.lat, lng: base.lng });
            setResolvingTz(true);
            const tz = await fetchTimezone(base.lat, base.lng, apiKey);
            setResolvingTz(false);
            setPicked((prev) => (prev ? { ...prev, timezone: tz } : prev));
          }
          // Start a fresh session token for the next search — per Places
          // billing rules, each getDetails "closes" the session.
          sessionToken.current =
            new google.maps.places.AutocompleteSessionToken();
        },
      );
    },
    [apiKey, ensurePlacesService],
  );

  // -------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------
  const handleSubmitGoogle = () => {
    if (!picked) return;
    onSelect(picked);
    onClose();
  };

  const handleSubmitManual = () => {
    onSelect({
      ...manual,
      lat: null,
      lng: null,
      timezone: '',
      formatted_address: [
        manual.street_address,
        manual.city,
        manual.state,
        manual.postal_code,
        manual.country,
      ]
        .filter(Boolean)
        .join(', '),
    });
    onClose();
  };

  const useManualFallback = !apiKey || !!loadError;

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Search to auto-fill street, city, state, country, postal code,
            and timezone.
          </Typography>
        </Box>
        <IconButton aria-label={t('common:actions.close')} size="small" onClick={onClose}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Hidden host DOM for PlacesService. Must be laid out (not
            display:none) so getDetails() attribution works. Hide
            off-screen instead. */}
        <Box
          ref={placesHostRef}
          sx={{
            position: 'absolute',
            width: 0,
            height: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        />

        {useManualFallback ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {loadError
                ? t('common:locationPicker.mapsFailedToLoad')
                : t('common:locationPicker.mapsKeyMissing')}
            </Alert>
            <ManualFields manual={manual} setManual={setManual} />
          </>
        ) : (
          <Stack spacing={2}>
            <Autocomplete
              fullWidth
              freeSolo
              loading={optionsLoading || !isLoaded}
              loadingText={isLoaded ? t('common:form.search') + '…' : t('attendance:geofences.loadingMap')}
              options={options}
              filterOptions={(x) => x} // server-side filter
              getOptionLabel={(opt) =>
                typeof opt === 'string' ? opt : opt.description
              }
              isOptionEqualToValue={(opt, val) =>
                typeof opt === 'string' || typeof val === 'string'
                  ? false
                  : opt.place_id === val.place_id
              }
              inputValue={inputValue}
              onInputChange={(_e, v) => setInputValue(v)}
              onChange={(_e, v) => {
                if (v && typeof v !== 'string') {
                  resolvePlace(v.place_id);
                }
              }}
              noOptionsText={
                inputValue.trim().length < 2
                  ? t('common:locationPicker.typeAtLeast2')
                  : t('common:locationPicker.noMatches')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder={t('attendance:geofences.addressPlaceholder')}
                  label={t('attendance:geofences.searchLabel')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <SearchOutlined fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {optionsLoading ? (
                          <CircularProgress size={18} sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Box
              sx={{
                height: 280,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.100',
                position: 'relative',
              }}
            >
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={center}
                  zoom={picked ? 15 : 3}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                  }}
                >
                  {picked && picked.lat !== null && picked.lng !== null && (
                    <MarkerF
                      position={{ lat: picked.lat, lng: picked.lng }}
                    />
                  )}
                </GoogleMap>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Loading map…
                  </Typography>
                </Box>
              )}
            </Box>

            {resolvingPlace && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Resolving place…
                </Typography>
              </Box>
            )}

            {picked && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.50',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <LocationOnOutlined
                    fontSize="small"
                    color="primary"
                    sx={{ mt: 0.25 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      {picked.formatted_address || '(no formatted address)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {picked.street_address || '—'} · {picked.city || '—'} ·{' '}
                      {picked.state || '—'} ·{' '}
                      {picked.country
                        ? `${picked.country}${
                            picked.postal_code
                              ? ` ${picked.postal_code}`
                              : ''
                          }`
                        : '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                    >
                      Timezone:{' '}
                      {resolvingTz
                        ? 'Resolving…'
                        : picked.timezone ||
                          '(not resolved — pick manually)'}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {useManualFallback ? (
          <Button
            onClick={handleSubmitManual}
            variant="contained"
            disabled={
              !manual.country && !manual.city && !manual.street_address
            }
          >
            Use this address
          </Button>
        ) : (
          <Button
            onClick={handleSubmitGoogle}
            variant="contained"
            disabled={!picked || resolvingPlace}
          >
            Use this location
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ManualFields: React.FC<{
  manual: ManualForm;
  setManual: React.Dispatch<React.SetStateAction<ManualForm>>;
}> = ({ manual, setManual }) => {
  const { t } = useTranslation();
  return (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <TextField
        label={t('attendance:geofences.streetAddress')}
        fullWidth
        size="small"
        value={manual.street_address}
        onChange={(e) =>
          setManual((m) => ({ ...m, street_address: e.target.value }))
        }
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('attendance:geofences.city')}
        fullWidth
        size="small"
        value={manual.city}
        onChange={(e) => setManual((m) => ({ ...m, city: e.target.value }))}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('attendance:geofences.stateRegion')}
        fullWidth
        size="small"
        value={manual.state}
        onChange={(e) => setManual((m) => ({ ...m, state: e.target.value }))}
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label={t('attendance:geofences.postalCode')}
        fullWidth
        size="small"
        value={manual.postal_code}
        onChange={(e) =>
          setManual((m) => ({ ...m, postal_code: e.target.value }))
        }
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <FormControl fullWidth size="small">
        <InputLabel id="manual-country-label">{t('attendance:geofences.country')}</InputLabel>
        <Select
          labelId="manual-country-label"
          label={t('attendance:geofences.country')}
          value={manual.country}
          onChange={(e) =>
            setManual((m) => ({ ...m, country: String(e.target.value) }))
          }
        >
          <MenuItem value="">
            <em>Select country</em>
          </MenuItem>
          {countries.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  </Grid>
);
};

const LocationPickerDialog: React.FC<LocationPickerDialogProps> = (props) => {
  if (!props.open) return null;
  const title = props.title ?? 'Pick a location';
  return (
    <GoogleMapsErrorBoundary
      fallback={(
        <GoogleMapsUnavailableDialog
          open
          title={title}
          onClose={props.onClose}
        />
      )}
    >
      <LocationPickerDialogLoaded {...props} title={title} />
    </GoogleMapsErrorBoundary>
  );
};

export default LocationPickerDialog;
